import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  INFORMATION_CONTENT_TYPES,
  INFORMATION_LIMITS,
  informationKindOf,
  isPublishedAt,
  maxBytesFor,
  type InformationItemView,
  type InformationKind,
} from "@shared/consumer-tvod";
import { PageHeader } from "../../components/PageHeader";
import { ItemFormSurface } from "../../components/EditDrawer";
import DangerConfirmDialog from "../../components/DangerConfirmDialog";
import { measure as measureMedia } from "../../components/MediaUploadDialog";
import {
  measurePdf as defaultMeasurePdf,
  type PdfMeasurer,
} from "../../lib/pdfMeasure";
import {
  foyerApi,
  ownerInformationFileUrl,
  ownerInformationPosterUrl,
} from "../../http/foyer";
import { tokens } from "../../theme";
import { useToast } from "../../components/toast";
import { OwnerGate } from "../../components/supplier/OwnerGate";
import { useFormat, type Formatters } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * The provider's "Information" rail (2026-10-09,
 * `docs/design/portal-information.md`): short clips and PDFs its SIGNED-IN
 * members see on the portal beside the catalogue. Per portal; edited here
 * by any of the provider's logins, like Plans.
 *
 * Measuring happens in the browser before upload, as in the media library:
 * a clip's length and a still frame (the length is REQUIRED - a clip this
 * browser cannot decode is one no member's browser can play), and a PDF's
 * page count and first page, drawn by PDF.js. The server re-checks all of it.
 */

export interface VideoMeasure {
  durationSeconds: number | null;
  posterDataUrl: string | null;
}
export type VideoMeasurer = (file: File) => Promise<VideoMeasure>;

const defaultMeasureVideo: VideoMeasurer = async (file) => {
  const m = await measureMedia(file);
  URL.revokeObjectURL(m.previewUrl);
  return { durationSeconds: m.durationSeconds, posterDataUrl: m.posterDataUrl };
};

const MB = 1024 * 1024;

const ERROR_CODES = [
  "title_required",
  "title_too_long",
  "description_too_long",
  "bad_date",
  "window_inverted",
  "unsupported_type",
  "content_mismatch",
  "too_large",
  "too_long",
  "duration_required",
  "too_many_items",
  "bad_order",
  "not_found",
] as const;
type ErrorCode = (typeof ERROR_CODES)[number];

const LIMIT_PARAMS = {
  limit: INFORMATION_LIMITS.title,
  videoMb: INFORMATION_LIMITS.videoBytes / MB,
  pdfMb: INFORMATION_LIMITS.pdfBytes / MB,
  minutes: INFORMATION_LIMITS.videoSeconds / 60,
};

function codeText(t: Translate, code: ErrorCode): string {
  const limit =
    code === "description_too_long"
      ? INFORMATION_LIMITS.description
      : code === "too_many_items"
        ? INFORMATION_LIMITS.itemsPerPortal
        : INFORMATION_LIMITS.title;
  return translateKey(t, `supplier:supplierInformationPage.errors.${code}`, {
    ...LIMIT_PARAMS,
    limit,
  });
}

function errorText(t: Translate, err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  return code && (ERROR_CODES as readonly string[]).includes(code)
    ? codeText(t, code as ErrorCode)
    : translateKey(t, "supplier:supplierInformationPage.errors.generic");
}

type ActionKey = "move" | "remove" | "add" | "save" | "replacePicture";

function failText(
  t: Translate,
  action: ActionKey,
  name: string,
  err: unknown
): string {
  return translateKey(t, "common:couldNotBecause", {
    action: translateKey(
      t,
      `supplier:supplierInformationPage.actions.${action}`,
      { name }
    ),
    reason: errorText(t, err).replace(/[.\s]+$/, ""),
  });
}

export function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function formatSize(t: Translate, fmt: Formatters, bytes: number): string {
  return bytes >= MB
    ? translateKey(t, "supplier:supplierInformationPage.sizeMb", {
        size: fmt.number(bytes / MB, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }),
      })
    : translateKey(t, "supplier:supplierInformationPage.sizeKb", {
        size: Math.max(1, Math.round(bytes / 1024)),
      });
}

/** `datetime-local` value (the browser's own zone) ⇄ ISO. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const t = new Date(value);
  return Number.isNaN(t.getTime()) ? null : t.toISOString();
}

type Status = "published" | "scheduled" | "ended";
export function statusOf(item: InformationItemView, now = new Date()): Status {
  if (isPublishedAt(item, now)) return "published";
  if (item.publishedFrom && new Date(item.publishedFrom) > now)
    return "scheduled";
  return "ended";
}
const STATUS_COLOR: Record<Status, "success" | "info" | "default"> = {
  published: "success",
  scheduled: "info",
  ended: "default",
};

function kindLine(
  t: Translate,
  item: Pick<InformationItemView, "kind" | "durationSeconds" | "pageCount">
) {
  const k = (name: string, params?: Record<string, unknown>) =>
    translateKey(t, `supplier:supplierInformationPage.kind.${name}`, params);
  if (item.kind === "video") {
    return item.durationSeconds
      ? k("videoWithDuration", {
          duration: formatDuration(item.durationSeconds),
        })
      : k("video");
  }
  return item.pageCount
    ? k("pdfWithPages", { count: item.pageCount })
    : k("pdf");
}

/* ── The page ──────────────────────────────────────────────────────────── */

export default function SupplierInformationPage({
  measureVideo = defaultMeasureVideo,
  measurePdf = defaultMeasurePdf,
}: {
  measureVideo?: VideoMeasurer;
  measurePdf?: PdfMeasurer;
} = {}) {
  const toast = useToast();
  const { t } = useTranslation(["supplier", "common"]);
  const fmt = useFormat();
  const [items, setItems] = useState<InformationItemView[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<InformationItemView | null>(null);
  const [removing, setRemoving] = useState<InformationItemView | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [playing, setPlaying] = useState<InformationItemView | null>(null);

  useEffect(() => {
    foyerApi
      .ownerInformation()
      .then((r) => setItems(r.items))
      .catch((err) => setLoadError(errorText(t, err)));
  }, [t]);

  const replace = (row: InformationItemView) =>
    setItems((cur) => (cur ?? []).map((i) => (i.id === row.id ? row : i)));

  async function move(index: number, by: -1 | 1) {
    if (!items) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    next.splice(index + by, 0, moved!);
    const before = items;
    setItems(next);
    setRowError(null);
    try {
      setItems(
        (await foyerApi.reorderOwnerInformation(next.map((i) => i.id))).items
      );
      toast.success(
        t(
          by === -1
            ? "supplierInformationPage.movedUp"
            : "supplierInformationPage.movedDown",
          { name: moved!.title }
        )
      );
    } catch (err) {
      setItems(before);
      setRowError(errorText(t, err));
      toast.error(failText(t, "move", moved!.title, err));
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      await foyerApi.deleteOwnerInformation(removing.id);
      setItems((cur) => (cur ?? []).filter((i) => i.id !== removing.id));
      setRemoving(null);
      toast.success(
        t("supplierInformationPage.removed", { name: removing.title })
      );
    } catch (err) {
      setRowError(errorText(t, err));
      toast.error(failText(t, "remove", removing.title, err));
      setRemoving(null);
    } finally {
      setRemoveBusy(false);
    }
  }

  const full = (items?.length ?? 0) >= INFORMATION_LIMITS.itemsPerPortal;

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t("supplierInformationPage.eyebrow")}
        title={t("supplierInformationPage.title")}
        subtitle={t("supplierInformationPage.subtitle")}
        actions={
          <OwnerGate resource="storefront" level="edit">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAdding(true)}
              disabled={!items || full}
            >
              {t("supplierInformationPage.addFile")}
            </Button>
          </OwnerGate>
        }
      />

      {loadError && <Alert severity="error">{loadError}</Alert>}
      {rowError && (
        <Alert severity="error" onClose={() => setRowError(null)}>
          {rowError}
        </Alert>
      )}
      {full && (
        <Alert severity="info">
          {t("supplierInformationPage.full", {
            limit: INFORMATION_LIMITS.itemsPerPortal,
          })}
        </Alert>
      )}

      {!items && !loadError && <CircularProgress size={28} />}

      {items && items.length === 0 && (
        <Box
          sx={{
            border: `1px dashed ${tokens.divider}`,
            borderRadius: 2,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6">
            {t("supplierInformationPage.emptyTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {t("supplierInformationPage.emptyBody", {
              minutes: INFORMATION_LIMITS.videoSeconds / 60,
              mb: INFORMATION_LIMITS.pdfBytes / MB,
            })}
          </Typography>
        </Box>
      )}

      {items && items.length > 0 && (
        <Stack
          spacing={1}
          component="ol"
          sx={{ listStyle: "none", p: 0, m: 0 }}
        >
          {items.map((item, i) => {
            const status = statusOf(item);
            return (
              <Stack
                key={item.id}
                component="li"
                direction="row"
                spacing={2}
                alignItems="center"
                data-testid={`info-row-${item.id}`}
                sx={{
                  p: 1.5,
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: 2,
                  bgcolor: tokens.surface,
                }}
              >
                <Thumb item={item} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight="fontWeightSemibold" noWrap>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {t(
                      item.description
                        ? "supplierInformationPage.metaWithDescription"
                        : "supplierInformationPage.meta",
                      {
                        kind: kindLine(t, item),
                        size: formatSize(t, fmt, item.sizeBytes),
                        description: item.description,
                      }
                    )}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={t(`supplierInformationPage.status.${status}`)}
                  color={STATUS_COLOR[status]}
                />
                <Stack direction="row">
                  <OwnerGate resource="storefront" level="edit">
                    <Tooltip title={t("supplierInformationPage.moveUp")}>
                      <span>
                        <IconButton
                          aria-label={t("supplierInformationPage.moveUpNamed", {
                            name: item.title,
                          })}
                          disabled={i === 0}
                          onClick={() => void move(i, -1)}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("supplierInformationPage.moveDown")}>
                      <span>
                        <IconButton
                          aria-label={t(
                            "supplierInformationPage.moveDownNamed",
                            { name: item.title }
                          )}
                          disabled={i === items.length - 1}
                          onClick={() => void move(i, 1)}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </OwnerGate>
                  {item.kind === "video" ? (
                    <Tooltip title={t("supplierInformationPage.play")}>
                      <IconButton
                        aria-label={t("supplierInformationPage.playNamed", {
                          name: item.title,
                        })}
                        onClick={() => setPlaying(item)}
                      >
                        <PlayCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t("common:buttons.download")}>
                      <IconButton
                        aria-label={t("supplierInformationPage.downloadNamed", {
                          name: item.title,
                        })}
                        component="a"
                        href={ownerInformationFileUrl(item.id, true)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <OwnerGate resource="storefront" level="edit">
                    <Tooltip title={t("common:buttons.edit")}>
                      <IconButton
                        aria-label={t("supplierInformationPage.editNamed", {
                          name: item.title,
                        })}
                        onClick={() => setEditing(item)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common:buttons.remove")}>
                      <IconButton
                        aria-label={t("supplierInformationPage.removeNamed", {
                          name: item.title,
                        })}
                        onClick={() => setRemoving(item)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </OwnerGate>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}

      {adding && (
        <AddDialog
          measureVideo={measureVideo}
          measurePdf={measurePdf}
          onClose={() => setAdding(false)}
          onAdded={(row) => {
            setItems((cur) => [...(cur ?? []), row]);
            setAdding(false);
          }}
        />
      )}

      {editing && (
        <EditDrawer
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={(row) => {
            replace(row);
            setEditing(null);
          }}
        />
      )}

      <DangerConfirmDialog
        open={!!removing}
        title={t("supplierInformationPage.removeTitle")}
        entityName={removing?.title ?? ""}
        body={t("supplierInformationPage.removeBody")}
        confirmLabel={t("common:buttons.remove")}
        busy={removeBusy}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />

      <Dialog
        open={!!playing}
        onClose={() => setPlaying(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{playing?.title}</DialogTitle>
        <DialogContent>
          {playing && (
            <video
              src={ownerInformationFileUrl(playing.id)}
              controls
              autoPlay
              style={{ width: "100%", borderRadius: 8, background: "#000" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

function Thumb({ item }: { item: InformationItemView }) {
  const [broken, setBroken] = useState(false);
  const box = {
    width: 112,
    height: 63,
    borderRadius: 1,
    flexShrink: 0,
    bgcolor: "action.hover",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  } as const;
  if (item.hasPoster && !broken) {
    return (
      <Box sx={box}>
        <img
          src={ownerInformationPosterUrl(item.id, item.updatedAt)}
          alt=""
          onError={() => setBroken(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>
    );
  }
  return (
    <Box sx={box} aria-hidden>
      {item.kind === "video" ? (
        <PlayCircleOutlineIcon color="action" />
      ) : (
        <PictureAsPdfOutlinedIcon color="action" />
      )}
    </Box>
  );
}

/* ── Add ───────────────────────────────────────────────────────────────── */

interface Chosen {
  file: File;
  kind: InformationKind;
  durationSeconds: number | null;
  pageCount: number | null;
  posterDataUrl: string | null;
}

/** Why a chosen file cannot be added, before anything is uploaded. */
export function rejectionOf(
  t: Translate,
  file: File,
  kind: InformationKind | null,
  durationSeconds: number | null
): string | null {
  if (!kind) return codeText(t, "unsupported_type");
  if (file.size > maxBytesFor(kind)) return codeText(t, "too_large");
  if (kind === "video") {
    if (durationSeconds === null) return codeText(t, "duration_required");
    if (durationSeconds > INFORMATION_LIMITS.videoSeconds + 1)
      return codeText(t, "too_long");
  }
  return null;
}

function titleFromName(name: string): string {
  return name
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .trim()
    .slice(0, INFORMATION_LIMITS.title);
}

function AddDialog({
  measureVideo,
  measurePdf,
  onClose,
  onAdded,
}: {
  measureVideo: VideoMeasurer;
  measurePdf: PdfMeasurer;
  onClose: () => void;
  onAdded: (row: InformationItemView) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["supplier", "common"]);
  const fmt = useFormat();
  const input = useRef<HTMLInputElement>(null);
  const [chosen, setChosen] = useState<Chosen | null>(null);
  const [measuring, setMeasuring] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function choose(file: File | undefined) {
    if (!file) return;
    setError(null);
    setChosen(null);
    const kind = informationKindOf(file.type);
    if (!kind) {
      setError(codeText(t, "unsupported_type"));
      return;
    }
    setMeasuring(true);
    try {
      let chosenNext: Chosen;
      if (kind === "video") {
        const m = await measureVideo(file);
        chosenNext = { file, kind, pageCount: null, ...m };
      } else {
        const m = await measurePdf(file);
        chosenNext = { file, kind, durationSeconds: null, ...m };
      }
      const why = rejectionOf(t, file, kind, chosenNext.durationSeconds);
      if (why) {
        setError(why);
        return;
      }
      setChosen(chosenNext);
      if (!title) setTitle(titleFromName(file.name));
    } finally {
      setMeasuring(false);
    }
  }

  async function upload() {
    if (!chosen) return;
    setError(null);
    setProgress(0);
    try {
      const row = await foyerApi.uploadOwnerInformation({
        file: chosen.file,
        title,
        description,
        publishedFrom: fromLocalInput(from),
        publishedTo: fromLocalInput(to),
        durationSeconds: chosen.durationSeconds,
        pageCount: chosen.pageCount,
        posterDataUrl: chosen.posterDataUrl,
        onProgress: setProgress,
      });
      toast.success(
        t("supplierInformationPage.added", {
          name: title || t("supplierInformationPage.fileFallback"),
        })
      );
      onAdded(row);
    } catch (err) {
      setError(errorText(t, err));
      toast.error(
        failText(
          t,
          "add",
          title || t("supplierInformationPage.fileFallbackLower"),
          err
        )
      );
      setProgress(null);
    }
  }

  const busy = progress !== null;

  return (
    <ItemFormSurface
      mode="create"
      onClose={onClose}
      title={t("supplierInformationPage.addTitle")}
      subtitle={t("supplierInformationPage.addSubtitle")}
      busy={busy}
      data-testid="info-add"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={busy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void upload()}
            disabled={!chosen || !title.trim() || busy}
            data-testid="info-add-save"
          >
            {t("common:buttons.add")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2}>
        <input
          ref={input}
          type="file"
          hidden
          accept={Object.values(INFORMATION_CONTENT_TYPES).join(",")}
          data-testid="info-file-input"
          onChange={(e) => void choose(e.target.files?.[0])}
        />
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 160,
              height: 90,
              borderRadius: 1,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {measuring ? (
              <CircularProgress size={24} />
            ) : chosen?.posterDataUrl ? (
              <img
                src={chosen.posterDataUrl}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : chosen?.kind === "pdf" ? (
              <PictureAsPdfOutlinedIcon color="action" />
            ) : (
              <PlayCircleOutlineIcon color="action" />
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Button
              variant="outlined"
              onClick={() => input.current?.click()}
              disabled={measuring || busy}
            >
              {chosen
                ? t("supplierInformationPage.chooseAnother")
                : t("supplierInformationPage.chooseFile")}
            </Button>
            {chosen && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
                noWrap
              >
                {t("supplierInformationPage.chosenLine", {
                  name: chosen.file.name,
                  kind: kindLine(t, chosen),
                  size: formatSize(t, fmt, chosen.file.size),
                })}
              </Typography>
            )}
          </Box>
        </Stack>

        <TextField
          label={t("common:labels.title")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          inputProps={{ maxLength: INFORMATION_LIMITS.title }}
          required
          fullWidth
        />
        <TextField
          label={t("common:labels.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: INFORMATION_LIMITS.description }}
          helperText={t("supplierInformationPage.descriptionHelp")}
          multiline
          minRows={2}
          fullWidth
        />
        <WindowFields from={from} to={to} setFrom={setFrom} setTo={setTo} />

        {busy && (
          <Box>
            <LinearProgress
              variant="determinate"
              value={Math.round((progress ?? 0) * 100)}
            />
            <Typography variant="caption" color="text.secondary">
              {t("supplierInformationPage.uploading", {
                percent: Math.round((progress ?? 0) * 100),
              })}
            </Typography>
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </ItemFormSurface>
  );
}

function WindowFields({
  from,
  to,
  setFrom,
  setTo,
}: {
  from: string;
  to: string;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
}) {
  const { t } = useTranslation("supplier");
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
      <TextField
        label={t("supplierInformationPage.showFrom")}
        type="datetime-local"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        InputLabelProps={{ shrink: true }}
        helperText={t("supplierInformationPage.fromHelp")}
        fullWidth
      />
      <TextField
        label={t("supplierInformationPage.showUntil")}
        type="datetime-local"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        InputLabelProps={{ shrink: true }}
        helperText={t("supplierInformationPage.untilHelp")}
        fullWidth
      />
    </Stack>
  );
}

/* ── Edit ──────────────────────────────────────────────────────────────── */

function EditDrawer({
  item,
  onClose,
  onSaved,
}: {
  item: InformationItemView;
  onClose: () => void;
  onSaved: (row: InformationItemView) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["supplier", "common"]);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [from, setFrom] = useState(toLocalInput(item.publishedFrom));
  const [to, setTo] = useState(toLocalInput(item.publishedTo));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState(item);
  const imageInput = useRef<HTMLInputElement>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const saved = await foyerApi.updateOwnerInformation(item.id, {
        title,
        description,
        publishedFrom: fromLocalInput(from),
        publishedTo: fromLocalInput(to),
      });
      toast.success(
        t("supplierInformationPage.saved", { name: title || item.title })
      );
      onSaved(saved);
    } catch (err) {
      setError(errorText(t, err));
      toast.error(failText(t, "save", title || item.title, err));
    } finally {
      setBusy(false);
    }
  }

  async function replaceImage(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setCurrent(await foyerApi.replaceOwnerInformationPoster(item.id, file));
      toast.success(
        t("supplierInformationPage.pictureReplaced", { name: item.title })
      );
    } catch (err) {
      setError(errorText(t, err));
      toast.error(failText(t, "replacePicture", item.title, err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ItemFormSurface
      mode="edit"
      onClose={() => onClose()}
      title={t("supplierInformationPage.editTitle")}
      subtitle={kindLine(t, item)}
      busy={busy}
      data-testid="info-edit"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} disabled={busy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={busy || !title.trim()}
            data-testid="info-edit-save"
          >
            {t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Thumb item={current} />
          <input
            ref={imageInput}
            type="file"
            hidden
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => void replaceImage(e.target.files?.[0])}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => imageInput.current?.click()}
            disabled={busy}
          >
            {t("supplierInformationPage.replaceImage")}
          </Button>
        </Stack>
        <TextField
          label={t("common:labels.title")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          inputProps={{ maxLength: INFORMATION_LIMITS.title }}
          required
          fullWidth
        />
        <TextField
          label={t("common:labels.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: INFORMATION_LIMITS.description }}
          multiline
          minRows={2}
          fullWidth
        />
        <WindowFields from={from} to={to} setFrom={setFrom} setTo={setTo} />
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </ItemFormSurface>
  );
}
